/**
 * @file StockWarehouseRow.tsx
 * @description Строка «фасовка на складе» в секции «Остатки» (phase19.14, W9).
 *
 * Вынесена из `StockBreakdownSection`: раскрытие партий и перестановка в
 * ячейку — своя маленькая история со своим состоянием, секции достаточно
 * знать «строка + колбэк обновления».
 */

import { ChevronDown, MoveRight } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { MoveLotBinDialog } from "../../../../components/wms/MoveLotBinDialog";
import type { SkuWarehouseStockDto } from "../../../../lib/api-product-stock";
import { warehouseKindLabel } from "../../../../lib/wms-warehouse-kind";

interface Props {
  row: SkuWarehouseStockDto;
  onLotMoved: () => void;
}

export function StockWarehouseRow({ row, onLotMoved }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [moving, setMoving] = useState<{
    lotId: string;
    batchNumber: string;
    binCode: string | null;
  } | null>(null);
  const lots = row.lots.filter((l) => l.lotId !== null);

  return (
    <div className="rounded-lg border border-on-surface/8 p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium">{row.warehouse.name}</span>
        {!row.warehouse.isSellable ? (
          <Badge variant="warning">
            {warehouseKindLabel(row.warehouse.kind)} — не для продажи
          </Badge>
        ) : null}
      </div>
      <div className="mt-1 text-xs tabular-nums text-on-surface-variant">
        физич. {row.quantity} · резерв {row.reserved} · доступно {row.available}
      </div>

      {lots.length > 0 ? (
        <>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="mt-2 inline-flex min-h-11 items-center gap-1 text-xs text-primary"
          >
            <ChevronDown
              className={cn("size-4 transition-transform", expanded && "rotate-180")}
            />
            Партии ({lots.length})
          </button>
          {expanded ? (
            <ul className="mt-1 flex flex-col gap-1.5">
              {lots.map((lot) => (
                <li
                  key={lot.lotId}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-surface-low px-2 py-1.5 text-xs"
                >
                  <span className="min-w-0">
                    {lot.batchNumber ?? "без номера"}
                    {lot.expiresAt
                      ? ` · до ${new Date(lot.expiresAt).toLocaleDateString("ru-RU")}`
                      : ""}
                    {lot.binCode ? ` · ячейка ${lot.binCode}` : " · без ячейки"}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="tabular-nums">{lot.quantity}</span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Переставить в ячейку"
                      title="Переставить в ячейку"
                      onClick={() =>
                        setMoving({
                          lotId: lot.lotId!,
                          batchNumber: lot.batchNumber ?? "без номера",
                          binCode: lot.binCode,
                        })
                      }
                    >
                      <MoveRight className="size-4" />
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}

      {moving ? (
        <MoveLotBinDialog
          lotId={moving.lotId}
          batchNumber={moving.batchNumber}
          warehouseId={row.warehouse.id}
          currentBinCode={moving.binCode}
          onMoved={onLotMoved}
          onClose={() => setMoving(null)}
        />
      ) : null}
    </div>
  );
}
